import { z } from 'zod';

import { Priority } from '@/models/appointment/Appointment.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

const appointment_type_schema = z.object({
    id: z.string().optional(),
    key: z.string(),
    name: z.string(),
    is_mapped: z.boolean(),
});

const symptom_schema = z.object({
    name: z.string(),
    key: z.string().optional(),
});

const booking_professional_schema = z.object({
    id: z.string().min(1),
    key: z.string(),
    type_key: z.string(),
    location_key: z.string().min(1),
});

const booking_patient_schema = z.object({
    id: z.string().min(1),
    is_eterno_user: z.boolean(),
    is_for_self: z.boolean(),
    is_returning: z.boolean(),
    is_public_insurance: z.boolean(),
    is_self_payer: z.boolean(),
    is_with_prescription: z.boolean(),
    is_private_insurance: z.boolean(),
    // Document[] in the DTO type is the DOM Document (apparent typo in model); not validated structurally.
    documents: z.array(z.unknown()),
    symptoms: z.array(symptom_schema),
});

const booking_pvs_schema = z
    .object({
        pvs_provider: z.enum(PVSProvider),
        doc_cirrus: z
            .object({
                patient_id: z.string().min(1),
                professional_id: z.string().min(1),
                schedule_type_id: z.string().min(1),
                calendar_id: z.string().min(1),
            })
            .optional(),
        data_cur: z
            .object({
                patient_id: z.string().min(1),
                professional_id: z.string().min(1),
                event_type_id: z.string().min(1),
                event_type_code: z.string().min(1),
                resource_id: z.string().min(1),
            })
            .optional(),
    })
    .refine(
        (pvs) => {
            if (pvs.pvs_provider === PVSProvider.DOC_CIRRUS) return Boolean(pvs.doc_cirrus);
            if (pvs.pvs_provider === PVSProvider.DATA_CUR) return Boolean(pvs.data_cur);
            return false; // TIGRAIS has no dedicated pvs branch in the booking DTO yet
        },
        { message: 'pvs branch must match pvs_provider' }
    );

export const book_appointment_dto_schema = z.object({
    professional: booking_professional_schema,
    appointment: z.object({
        types: z.array(appointment_type_schema).min(1),
        is_eterno_user: z.boolean(),
        is_at_location: z.boolean(),
        is_online: z.boolean(),
        slot: z.iso.datetime(),
        duration: z.object({
            value: z.number().positive(),
            units: z.literal('MINUTES'),
        }),
        priority: z.enum(Priority),
        description: z.string().optional(),
        info: z.string().optional(),
    }),
    patient: booking_patient_schema,
    pvs: booking_pvs_schema,
});
